import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataView,
    ArrayDataSource,
    DataExtractor,
} from "@impero-com/common/utils/dataView";
import { unwrapOrThrow } from "@impero-com/common/utils/error";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    SubsequentAssignmentKind,
    UserGroupView,
    UserOverview,
    UserTagView,
    EntityView,
} from "api/generated/model";
import { ASSIGNEE_CATEGORY_METHODS } from "utils/assigneeTags";
import { ControlAssignmentGroup } from "utils/controls/assignmentUtils";
import { ASSIGNMENT_EXTRACTOR_KEYS } from "utils/controls/extractorKeys";
import {
    TagLookupData,
    AbstractTagCategory,
    createTagNameLookupFn,
} from "utils/tag";
import { isNotUndefined } from "utils/typePredicates";
import { mapUserGroupViewToUserGroupOverviewResource } from "utils/userGroup";

import {
    AssignmentRow,
    AssignmentRowUserGroup,
    ControlAssignmentResources,
    ExportedAssignmentRow,
    ReviewerType,
} from "./types";

function assignmentGroupToAssignmentRows(
    resources: ControlAssignmentResources
): (row: ControlAssignmentGroup) => AssignmentRow {
    const userOverviewMap = resources.users.reduce(
        (acc: Record<number, UserOverview>, user) => {
            acc[user.resource.id] = user.resource;
            return acc;
        },
        {}
    );
    const userGroupOverviewMap = resources.userGroups.reduce(
        (acc: Record<number, UserGroupView>, userGroup) => {
            acc[userGroup.resource.id] = userGroup.resource;
            return acc;
        },
        {}
    );

    return (row: ControlAssignmentGroup) => {
        const { firstReviewer, finalReviewer } = getUserReviewers(
            row,
            userOverviewMap,
            userGroupOverviewMap
        );
        const tagIds = row.tagIds ?? [];
        const assignee = row.initialAssignment;
        const hasNotificationRecipients =
            (row.notificationRecipients?.["AllCompleted"]?.length ?? 0) > 0;
        switch (assignee.type) {
            case "User": {
                const userOverview = unwrapOrThrow(
                    userOverviewMap[assignee.userId],
                    `Assignee with userId ${assignee.userId} isn't present in the linkedResources`
                );
                return {
                    type: "User",
                    id: row.id,
                    userOverview,
                    tagIds,
                    firstReviewer,
                    finalReviewer,
                    notify: hasNotificationRecipients,
                    entityId: row.entityId,
                };
            }
            case "Group": {
                const userGroup = unwrapOrThrow(
                    userGroupOverviewMap[assignee.userGroupId],
                    `Assignee with userGroupId ${assignee.userGroupId} isn't present in the linkedResources`
                );
                return {
                    type: "Group",
                    id: row.id,
                    userGroup: {
                        id: userGroup.id,
                        name: userGroup.name,
                        description: userGroup.description,
                        created: userGroup.created,
                        createdBy: userGroup.createdBy,
                        userGroupUsers: userGroup.userGroupUsers
                            .map((ugu) => userOverviewMap[ugu.userId])
                            // In Typeahead we do not really care about user group users, and modifying overviewMap whenever user types something
                            // is prone to error, so lets just filter out all `undefined` entries
                            .filter(isNotUndefined),
                    } satisfies AssignmentRowUserGroup,
                    tagIds,
                    firstReviewer,
                    finalReviewer,
                    notify: hasNotificationRecipients,
                    entityId: row.entityId,
                };
            }
            case "Pool": {
                const defaultClaimantUser = assignee.defaultClaimantId
                    ? unwrapOrThrow(
                          userOverviewMap[assignee.defaultClaimantId],
                          `Default claimant with id ${assignee.defaultClaimantId} isn't present in the linkedResources`
                      )
                    : null;
                const userGroup = unwrapOrThrow(
                    userGroupOverviewMap[assignee.userGroupId],
                    `Assignee with userGroupId ${assignee.userGroupId} isn't present in the linkedResources`
                );
                return {
                    type: "Pool",
                    id: row.id,
                    userGroupOverviewResource:
                        mapUserGroupViewToUserGroupOverviewResource(userGroup),
                    defaultClaimantUser,
                    tagIds,
                    firstReviewer,
                    finalReviewer,
                    notify: hasNotificationRecipients,
                    entityId: row.entityId,
                };
            }
            default:
                return assertNever(assignee);
        }
    };
}

type AssignmentRowExtractor = DataExtractor<AssignmentRow, any, any>;
function buildAssignmentRowExtractors(
    controlEntitiesEnabled: boolean
): AssignmentRowExtractor[] {
    const baseAssignmentRowExtractors: AssignmentRowExtractor[] = [
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.RESPONSIBLE,
            label: t("ControlResult_Assignee"),
            getValue: (row) => row,
            compare: cmpResponsible,
            asString: (_row) => "",
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.FIRST_REVIEWER,
            label: t("ControlResult_Reviewer"),
            getValue: (row) => row,
            compare: cmpFirstReviewer,
            asString: (_row) => "",
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.SECOND_REVIEWER,
            label: t("Control_SecondReviewer"),
            getValue: (row) => row,
            compare: cmpSecondReviewer,
            asString: (_row) => "",
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.TAGS,
            label: t("Control_Assignment_Tags"),
            getValue: (row) => row,
            compare: Cmp.map((row) => row.id, Cmp.string()),
            asString: (_row) => "",
        },
    ];

    const entityExtractor: AssignmentRowExtractor = {
        id: ASSIGNMENT_EXTRACTOR_KEYS.ENTITY,
        label: t("Control_Assignment_Entity_Label"),
        getValue: (row) => row,
        compare: Cmp.map((row) => row.id, Cmp.string()),
        asString: (_row) => "",
    };

    return controlEntitiesEnabled
        ? baseAssignmentRowExtractors.toSpliced(3, 0, entityExtractor) // if ControlEntities the backend feature flag is enabled, we should inject the entity extractor here before tags extractor
        : baseAssignmentRowExtractors;
}

function formatAssignee(row: ExportedAssignmentRow): string {
    switch (row.type) {
        case "GroupIndividual": {
            return formatUserName(row.userOverview);
        }
        case "User": {
            return formatUserName(row.userOverview);
        }
        case "Pool": {
            return "";
        }
        default:
            return assertNever(row);
    }
}

function formatAssigneeGroup(row: ExportedAssignmentRow): string {
    switch (row.type) {
        case "User": {
            return "";
        }
        case "GroupIndividual": {
            return row.userGroup.name;
        }
        case "Pool": {
            return row.userGroupOverviewResource.userGroup.name;
        }
        default:
            return assertNever(row);
    }
}

function formatFirstReviewer(row: ExportedAssignmentRow): string {
    const first = getFirstReviewer(row);
    if (first === null) {
        return "";
    }
    return getUserOrGroupName(first);
}

function formatSecondReviewer(row: ExportedAssignmentRow): string {
    const second = getSecondReviewer(row);
    if (second === null) {
        return "";
    }
    return getUserOrGroupName(second);
}

function formatEntity(
    row: ExportedAssignmentRow,
    entitiesById: Map<number, EntityView>
): string {
    if (row.entityId === undefined) {
        return "";
    }
    return entitiesById.get(row.entityId)?.name ?? "";
}

/**
 * Data extractors used in export to Excel.
 * Note, assignee column is split into "Assignee" and "AssigneeGroup".
 *
 * When `entitiesById` is provided, an `Entity`
 * column is added that resolves each assignment's `entityId` to its name.
 */
function buildAssignmentRowExportExtractors(
    entitiesById?: Map<number, EntityView>
): DataExtractor<ExportedAssignmentRow, any, any>[] {
    const baseExtractors: DataExtractor<ExportedAssignmentRow, any, any>[] = [
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.EXPORT_ASSIGNEE,
            label: t("Controls_Export_Assignee"),
            getValue: (row) => row,
            compare: (a, b) => cmpResponsibleForExport(a, b, "user"),
            asString: formatAssignee,
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.EXPORT_ASSIGNEE_GROUP,
            label: t("Controls_Export_Assignee_Group"),
            getValue: (row) => row,
            compare: (a, b) => cmpResponsibleForExport(a, b, "group"),
            asString: formatAssigneeGroup,
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.EXPORT_TAGS,
            label: t("Tags"),
            getValue: (row) => row.tagIds,
            compare: Cmp.map((row) => row.tagIds.length, Cmp.primitive),
            asString: (_row) => {
                // Tags extractor is later replaced by per-tag-category-extractor which supports asString
                throw new Error(
                    "Unexpected call of asString() on tags extractor."
                );
            },
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.FIRST_REVIEWER,
            label: t("ControlResult_Reviewer"),
            getValue: (row) => row,
            compare: cmpFirstReviewer,
            asString: formatFirstReviewer,
        },
        {
            id: ASSIGNMENT_EXTRACTOR_KEYS.SECOND_REVIEWER,
            label: t("Control_SecondReviewer"),
            getValue: (row) => row,
            compare: cmpSecondReviewer,
            asString: formatSecondReviewer,
        },
    ];

    if (entitiesById === undefined) {
        return baseExtractors;
    }

    const entityExtractor: DataExtractor<ExportedAssignmentRow, any, any> = {
        id: ASSIGNMENT_EXTRACTOR_KEYS.EXPORT_ENTITY,
        label: t("Control_Management_Entity"),
        getValue: (row) => row,
        compare: Cmp.map(
            (row) => formatEntity(row, entitiesById),
            Cmp.string()
        ),
        asString: (row) => formatEntity(row, entitiesById),
    };

    return [...baseExtractors, entityExtractor];
}

function buildAssignmentRowTagsExtractors<
    TAG extends UserTagView,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): DataExtractor<ExportedAssignmentRow, any, any>[] {
    const tagNameLookup = createTagNameLookupFn(allTags);
    const tagsById = new Map(
        allTags.content.map(({ resource }) => [resource.id, resource])
    );
    const allCategories: AbstractTagCategory[] = allTags.linkedResources.map(
        ({ id, name }) => ({ id, name })
    );
    return allCategories.map((category) => {
        const extractorId: string =
            ASSIGNEE_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: category.id,
                name: category.name,
            });

        return {
            id: extractorId,
            label: category.name,
            getValue: (row) => row.tagIds,
            compare: (_a, _b) => 0,
            asString: (row) => {
                // asString is used in exporting tags to Excel:
                return row.tagIds
                    .flatMap((tagId) => tagsById.get(tagId) ?? [])
                    .filter((tag) => tag.tagCategoryId === category.id)
                    .map((tag) => tag.name)
                    .join(", ");
            },
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString: tagNameLookup,
            },
        };
    });
}

function mapPropsToTableDataSource(
    assignments: ControlAssignmentGroup[],
    resources: ControlAssignmentResources,
    controlEntitiesEnabled: boolean,
    extractors: DataExtractor<AssignmentRow, any, any>[] = []
): DataView<AssignmentRow> {
    const assignmentMap = assignmentGroupToAssignmentRows(resources);

    const assignmentRows: AssignmentRow[] = assignments.map(
        (row: ControlAssignmentGroup) => {
            return assignmentMap(row);
        }
    );
    assignmentRows.sort(
        Cmp.chain([
            cmpResponsible,
            cmpFirstReviewer,
            cmpSecondReviewer,
            Cmp.map((row) => row.id, Cmp.string()),
        ])
    );
    const dataSource = new ArrayDataSource<AssignmentRow>(
        assignmentRows,
        (row) => row.id
    );
    return {
        dataSource,
        extractorGroups: [
            {
                id: "standard",
                label: "Standard",
                extractors: [
                    ...buildAssignmentRowExtractors(controlEntitiesEnabled),
                    ...extractors,
                ],
            },
        ],
        sorters: [
            {
                extractorId: ASSIGNMENT_EXTRACTOR_KEYS.RESPONSIBLE,
                direction: "ASC",
            },
            {
                extractorId: ASSIGNMENT_EXTRACTOR_KEYS.FIRST_REVIEWER,
                direction: "ASC",
            },
            {
                extractorId: ASSIGNMENT_EXTRACTOR_KEYS.SECOND_REVIEWER,
                direction: "ASC",
            },
            {
                extractorId: ASSIGNMENT_EXTRACTOR_KEYS.ENTITY,
                direction: "ASC",
            },
        ],
        defaultCompareItems: cmpResponsible,
    };
}

interface UserReviewers {
    firstReviewer: ReviewerType | null;
    finalReviewer: ReviewerType | null;
}

function getUserReviewers(
    row: ControlAssignmentGroup,
    userOverviewMap: Record<number, UserOverview>,
    userGroupOverviewMap: Record<number, UserGroupView>
): UserReviewers {
    let firstReviewer = null;
    let finalReviewer = null;
    if (row.firstReviewer) {
        firstReviewer = getReviewer(
            row.firstReviewer,
            userOverviewMap,
            userGroupOverviewMap
        );
        if (row.secondReviewer) {
            finalReviewer = getReviewer(
                row.secondReviewer,
                userOverviewMap,
                userGroupOverviewMap
            );
        }
    }

    return { firstReviewer, finalReviewer };
}

function getReviewer(
    reviewer: SubsequentAssignmentKind,
    userOverviewMap: Record<number, UserOverview>,
    userGroupOverviewMap: Record<number, UserGroupView>
): ReviewerType {
    switch (reviewer.type) {
        case "User": {
            return {
                type: "User",
                userOverview: unwrapOrThrow(
                    userOverviewMap[reviewer.userId],
                    `Reviewer with userId ${reviewer.userId} isn't present in the linkedResources`
                ),
            };
        }
        case "Pool": {
            const userGroup = unwrapOrThrow(
                userGroupOverviewMap[reviewer.userGroupId],
                `Reviewer with userGroupId ${reviewer.userGroupId} isn't present in the linkedResources`
            );
            return {
                type: "Pool",
                userGroupOverviewResource:
                    mapUserGroupViewToUserGroupOverviewResource(userGroup),
            };
        }
        default:
            return assertNever(reviewer);
    }
}

const RANK_RESPONSIBLE = {
    User: 1,
    Group: 2,
    Pool: 3,
};

function cmpResponsible(a: AssignmentRow, b: AssignmentRow): number {
    const rankForA = RANK_RESPONSIBLE[a.type];
    const rankForB = RANK_RESPONSIBLE[b.type];

    if (rankForA < rankForB) {
        return -1;
    } else if (rankForA > rankForB) {
        return 1;
    } else {
        // types are equal so we should sort by user name or usergroup name
        let nameComparisonResult = 0;
        switch (a.type) {
            case "User":
                nameComparisonResult =
                    compareUserNameOrUserGroupNameWithOtherAssignmentRow(
                        formatUserName(a.userOverview),
                        b
                    );
                break;
            case "Group":
                nameComparisonResult =
                    compareUserNameOrUserGroupNameWithOtherAssignmentRow(
                        a.userGroup.name,
                        b
                    );
                break;
            case "Pool":
                nameComparisonResult =
                    compareUserNameOrUserGroupNameWithOtherAssignmentRow(
                        a.userGroupOverviewResource.userGroup.name,
                        b
                    );
                break;
            default:
                assertNever(a);
        }
        // if type and name are equal then we compare by id
        return nameComparisonResult;
    }
}

const RANK_RESPONSIBLE_FOR_EXPORT = {
    User: 1,
    GroupIndividual: 2,
    Pool: 3,
};

function cmpResponsibleForExport(
    a: ExportedAssignmentRow,
    b: ExportedAssignmentRow,
    compareIndividualBy: "user" | "group"
): number {
    const rankForA = RANK_RESPONSIBLE_FOR_EXPORT[a.type];
    const rankForB = RANK_RESPONSIBLE_FOR_EXPORT[b.type];

    if (rankForA < rankForB) {
        return -1;
    } else if (rankForA > rankForB) {
        return 1;
    } else {
        // types are equal so we should sort by user name or usergroup name
        let nameComparisonResult = 0;
        switch (a.type) {
            case "User":
                nameComparisonResult =
                    compareUserNameOrUserGroupNameWithOtherExportedAssignmentRow(
                        formatUserName(a.userOverview),
                        b
                    );
                break;
            case "GroupIndividual": {
                switch (compareIndividualBy) {
                    case "user":
                        nameComparisonResult =
                            compareUserNameOrUserGroupNameWithOtherExportedAssignmentRow(
                                formatUserName(a.userOverview),
                                b
                            );
                        break;
                    case "group":
                        nameComparisonResult =
                            compareUserNameOrUserGroupNameWithOtherExportedAssignmentRow(
                                a.userGroup.name,
                                b
                            );
                        break;
                    default:
                        assertNever(compareIndividualBy);
                }
                break;
            }
            case "Pool":
                nameComparisonResult =
                    compareUserNameOrUserGroupNameWithOtherExportedAssignmentRow(
                        a.userGroupOverviewResource.userGroup.name,
                        b
                    );
                break;
            default:
                assertNever(a);
        }
        // if type and name are equal then we compare by id
        return nameComparisonResult;
    }
}

function cmpFirstReviewer(
    assignmentRowA: AssignmentRow | ExportedAssignmentRow,
    assignmentRowB: AssignmentRow | ExportedAssignmentRow
): number {
    return cmpReviewer(
        getFirstReviewer(assignmentRowA),
        getFirstReviewer(assignmentRowB)
    );
}

function cmpSecondReviewer(
    assignmentRowA: AssignmentRow | ExportedAssignmentRow,
    assignmentRowB: AssignmentRow | ExportedAssignmentRow
): number {
    return cmpReviewer(
        getSecondReviewer(assignmentRowA),
        getSecondReviewer(assignmentRowB)
    );
}

function cmpReviewer(
    reviewerToCompareA: ReviewerType | null,
    reviewerToCompareB: ReviewerType | null
) {
    // sorting by reviewer existence
    if (!reviewerToCompareA && !reviewerToCompareB) {
        return 0;
    } else if (reviewerToCompareA && !reviewerToCompareB) {
        return -1;
    } else if (!reviewerToCompareA && reviewerToCompareB) {
        return 1;
    } else {
        // sorting by reviewer type
        const rank: Record<string, number> = {
            User: 1,
            Pool: 2,
        };
        const rankForA = rank[reviewerToCompareA!.type];
        const rankForB = rank[reviewerToCompareB!.type];

        if (rankForA < rankForB) {
            return -1;
        } else if (rankForA > rankForB) {
            return 1;
        } else {
            // sorting by user/usergroup name
            const userNameOrGroupNameToCompareForA = getUserOrGroupName(
                reviewerToCompareA!
            );
            const userNameOrGroupNameToCompareForB = getUserOrGroupName(
                reviewerToCompareB!
            );

            const nameComparisonResult =
                userNameOrGroupNameToCompareForA.localeCompare(
                    userNameOrGroupNameToCompareForB
                );

            return nameComparisonResult;
        }
    }
}

function compareUserNameOrUserGroupNameWithOtherAssignmentRow(
    userNameOrUserGroupName: string,
    assignmentRow: AssignmentRow
): number {
    switch (assignmentRow.type) {
        case "User":
            return userNameOrUserGroupName.localeCompare(
                formatUserName(assignmentRow.userOverview)
            );
        case "Group":
            return userNameOrUserGroupName.localeCompare(
                assignmentRow.userGroup.name
            );
        case "Pool":
            return userNameOrUserGroupName.localeCompare(
                assignmentRow.userGroupOverviewResource.userGroup.name
            );
        default:
            return assertNever(assignmentRow);
    }
}

function compareUserNameOrUserGroupNameWithOtherExportedAssignmentRow(
    userNameOrUserGroupName: string,
    assignmentRow: ExportedAssignmentRow
): number {
    switch (assignmentRow.type) {
        case "User":
        case "GroupIndividual":
            return userNameOrUserGroupName.localeCompare(
                formatUserName(assignmentRow.userOverview)
            );
        case "Pool":
            return userNameOrUserGroupName.localeCompare(
                assignmentRow.userGroupOverviewResource.userGroup.name
            );
        default:
            return assertNever(assignmentRow);
    }
}

function getFirstReviewer(
    assignee: AssignmentRow | ExportedAssignmentRow
): ReviewerType | null {
    if (assignee.firstReviewer) {
        return assignee.firstReviewer;
    } else if (assignee.finalReviewer) {
        return assignee.finalReviewer;
    } else {
        return null;
    }
}

function getSecondReviewer(
    assignee: AssignmentRow | ExportedAssignmentRow
): ReviewerType | null {
    return assignee.firstReviewer && assignee.finalReviewer
        ? assignee.finalReviewer
        : null;
}

function getUserOrGroupName(reviewer: ReviewerType): string {
    switch (reviewer.type) {
        case "User":
            return formatUserName(reviewer.userOverview);
        case "Pool":
            return reviewer.userGroupOverviewResource.userGroup.name;
    }
}

export {
    assignmentGroupToAssignmentRows,
    buildAssignmentRowExportExtractors,
    buildAssignmentRowTagsExtractors,
    mapPropsToTableDataSource,
};
